/**
 * Practice launched from the study chat (TANAI-2616): the URL of the launcher tab, the tab marker
 * that isolates that tab from the study tabs and the launch context the practice tab reads.
 *
 * Everything lives in `sessionStorage`, which belongs to a single tab and survives its reloads and
 * in-app navigations. Dependency-free on purpose: the app bootstrap imports it before React mounts.
 */
import type { PracticeProposalTargetType } from '../types/practiceProposal';

export const PRACTICE_LAUNCH_PATH_PREFIX = '/practice-launch/';

/** Marker of a tab opened by the study chat card. Never removed: it lasts the whole tab life. */
export const PRACTICE_LAUNCH_TAB_KEY = 'tangerine_practice_launch_tab';

const PRACTICE_LAUNCH_CONTEXT_KEY_PREFIX = 'tangerine_practice_launch_context:';

const CONVERSATION_ID_PARAM = 'conversationId';
const INTENT_KEY_PARAM = 'key';
const NEW_ATTEMPT_PARAM = 'newAttempt';
const TARGET_TYPE_PARAM = 'targetType';
const TARGET_TYPES: readonly PracticeProposalTargetType[] = ['LESSONS', 'KC'];

export const PRACTICE_LAUNCH_ORIGIN = 'study-chat';

// Format check only: tangerine-ai ids are time-ordered UUID v7, which `validate` from the `uuid`
// package (9.x) rejects, and the server validates them anyway.
const UUID_FORMAT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuidFormat(value: string | null | undefined): boolean {
  return Boolean(value) && UUID_FORMAT.test(value as string);
}

const PRACTICE_TAB_NAME_PREFIX = 'tangerine-practice-';

export type PracticeLaunchTabMarker = {
  conversationId: string | null;
  proposalId: string;
};

export type PracticeLaunchContext = {
  attemptId: string;
  conversationId: string;
  origin: typeof PRACTICE_LAUNCH_ORIGIN;
  proposalId: string;
};

export type PracticeLaunchRequest = {
  conversationId: string;
  intentKey: string;
  isNewAttempt: boolean;
  proposalId: string;
  /** Only feeds the analytics of the launcher: the attempts endpoint does not return it. */
  targetType: PracticeProposalTargetType | null;
};

type LocationLike = {
  pathname: string;
  search: string;
};

function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the tab keeps working, only the isolation or the return to the
    // tutor degrade, which is what a tab without the marker already does.
  }
}

function stripBasename(pathname: string, basename: string): string {
  if (basename && basename !== '/' && pathname.startsWith(basename)) {
    return pathname.slice(basename.length) || '/';
  }
  return pathname;
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * `window.open` target of the practice tab of a proposal. A named target makes a second click on
 * the same card navigate the tab it already opened instead of opening another one.
 */
export function getPracticeTabName(proposalId: string): string {
  return `${PRACTICE_TAB_NAME_PREFIX}${proposalId}`;
}

/** Whether a browser pathname (it still carries the router basename) is the launcher route. */
export function isPracticeLaunchPath(pathname: string, basename = ''): boolean {
  return stripBasename(pathname, basename).startsWith(PRACTICE_LAUNCH_PATH_PREFIX);
}

/** Router-relative location of the launcher (`history.createHref` adds the router basename). */
export function buildPracticeLaunchLocation({ conversationId, intentKey, isNewAttempt, proposalId, targetType }: PracticeLaunchRequest): LocationLike {
  const params = new URLSearchParams({ [CONVERSATION_ID_PARAM]: conversationId, [INTENT_KEY_PARAM]: intentKey });
  if (isNewAttempt) params.set(NEW_ATTEMPT_PARAM, 'true');
  if (targetType) params.set(TARGET_TYPE_PARAM, targetType);

  return {
    pathname: `${PRACTICE_LAUNCH_PATH_PREFIX}${encodeURIComponent(proposalId)}`,
    search: `?${params.toString()}`,
  };
}

/** Reads the launcher query string; missing values come back empty so the launcher can reject them. */
export function parsePracticeLaunchSearch(search: string): Omit<PracticeLaunchRequest, 'proposalId'> {
  const params = new URLSearchParams(search);
  const targetType = params.get(TARGET_TYPE_PARAM) as PracticeProposalTargetType | null;
  return {
    conversationId: params.get(CONVERSATION_ID_PARAM) ?? '',
    intentKey: params.get(INTENT_KEY_PARAM) ?? '',
    isNewAttempt: params.get(NEW_ATTEMPT_PARAM) === 'true',
    targetType: TARGET_TYPES.includes(targetType) ? targetType : null,
  };
}

/**
 * Called by the app bootstrap BEFORE the chat provider mounts, so the provider already starts
 * isolated on the very first render of a launcher tab. Writing it from the launcher page would be
 * too late: the provider resolves its persisted state (and the new-day clear) on mount.
 */
export function markPracticeLaunchTab(location: LocationLike, basename = ''): void {
  if (!isPracticeLaunchPath(location.pathname, basename)) return;

  const pathname = stripBasename(location.pathname, basename);
  const proposalId = decodeSegment(pathname.slice(PRACTICE_LAUNCH_PATH_PREFIX.length).split('/')[0] ?? '');
  const { conversationId } = parsePracticeLaunchSearch(location.search);
  const marker: PracticeLaunchTabMarker = { proposalId, conversationId: conversationId || null };

  writeJson(PRACTICE_LAUNCH_TAB_KEY, marker);
}

export function getPracticeLaunchTabMarker(): PracticeLaunchTabMarker | null {
  return readJson<PracticeLaunchTabMarker>(PRACTICE_LAUNCH_TAB_KEY);
}

/**
 * Whether this tab was opened from a study chat card. Such a tab never writes nor reads the chat
 * state shared with the study tabs, for its whole life, whatever route it shows.
 */
export function isPracticeLaunchTab(): boolean {
  return getPracticeLaunchTabMarker() !== null;
}

export function savePracticeLaunchContext(sessionId: string, context: PracticeLaunchContext): void {
  writeJson(`${PRACTICE_LAUNCH_CONTEXT_KEY_PREFIX}${sessionId}`, context);
}

/**
 * Keyed by session so it survives the navigations that rebuild the practice URL (next activity,
 * summary) and the reloads.
 */
export function getPracticeLaunchContext(sessionId: string | null | undefined): PracticeLaunchContext | null {
  if (!sessionId) return null;
  const context = readJson<PracticeLaunchContext>(`${PRACTICE_LAUNCH_CONTEXT_KEY_PREFIX}${sessionId}`);
  return context?.origin === PRACTICE_LAUNCH_ORIGIN ? context : null;
}

/**
 * Browsers only let a script close a tab that a script opened, which is how the study chat card
 * opens this one. Callers show a «go back to the tutor» screen for the case the tab stays open.
 */
export function closePracticeTab(): void {
  window.close();
}
